import React, { useMemo } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { Image } from '@scentregroup/ui'
import displayNameForProduct from '../../helpers/display-name-for-product'
import { getProductListingIssue } from '../../helpers/stock-level-issue-messages'
import { displayStringForPriceRange } from '../../helpers/product-price'
import { displayStringForDiscountPercentageRange } from '../../helpers/percentage'
import { stringToSlug } from '../../helpers/slug'
import { IPercentageRange, IProductPriceRange } from '../../types'
import { IProduct } from '../products-list'
import ConditionalIssueText from '../conditional-issue-text'
import style from './index.module.scss'

interface IShop {
  id: number
  name: string
  retailer?: {
    title: string
  }
}

export interface IProductCardProps {
  product: IProduct
  shop: IShop
  priceRange?: IProductPriceRange
  discountedPriceRange?: IProductPriceRange
  discountPercentageRange?: IPercentageRange
}

// This duplicates what's in /src/client/path.ts of the checkout app
const PRODUCT = `/shop/product/:shopId/:productId/:productTitle`

const storeName = (shop: IShop): string => {
  if (shop.retailer?.title && shop.retailer.title.length > 0) {
    return shop.retailer.title
  } else {
    return shop.name
  }
}

export const ProductCard: React.FunctionComponent<IProductCardProps> = ({
  product,
  shop,
  priceRange,
  discountedPriceRange,
  discountPercentageRange,
}) => {
  const issue = useMemo(() => getProductListingIssue(product?.issues), [
    product?.issues,
  ])

  return (
    <div className={style.productCard}>
      <Link
        className={style.productLinkWrapper}
        to={generatePath(`${PRODUCT}`, {
          productTitle: product.brand?.name
            ? stringToSlug(`${product.brand.name}-${product.name}`)
            : stringToSlug(product.name),
          productId: product.variantGroupCode,
          shopId: `${shop.id}`,
        })}
      >
        <Image
          className={style.productImage}
          imageUrl={product.image}
          alt={''}
          ratio="poster"
          bgSize="contain"
          type="mobile"
        />
        <div className={style.productName}>
          {displayNameForProduct(product)}
        </div>
        <div className={style.price}>
          {displayStringForPriceRange(discountedPriceRange ?? priceRange)}
        </div>
      </Link>
      <div className={style.productRetailer}>
        Sold by&nbsp;
        {storeName(shop)}
      </div>
      <ConditionalIssueText issue={issue} wrapperClass={style.issueWrapper} />
      {discountedPriceRange && (
        <div className={style.originalPrice}>
          {displayStringForPriceRange(priceRange)}
        </div>
      )}
      {discountPercentageRange && (
        <div className={style.discount}>
          {displayStringForDiscountPercentageRange(discountPercentageRange)}
        </div>
      )}
    </div>
  )
}

export default ProductCard
